import { memo, useCallback, useEffect } from 'react';
import { cn, Switch, Gallery } from '@divlab/divanui';

import * as PageProductStore from '@Stores/PageProduct';
import { useDeps } from '@Contexts/DI';
import Button from '@UI/Button';
import styles from './Buttons.module.css';

import type { FC, HTMLAttributes, MouseEvent, PropsWithChildren } from 'react';

interface ContainerProps extends PropsWithChildren {
  className?: string;
  scrollable?: boolean;
}

export interface ButtonsProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  cnButton?: string;
  size?: 'm' | 's';
  hasCylindo: boolean;
  scrollable?: boolean;
  idViewerModel: string;
}

const Container: FC<ContainerProps> = (props) => {
  const { scrollable, children, ...restProps } = props;

  return scrollable ? (
    <Gallery spaceBetween={15} {...restProps} cnGallery={styles.gallery}>
      {children}
    </Gallery>
  ) : (
    <div {...restProps}>{children}</div>
  );
};

const Buttons: FC<ButtonsProps> = (props) => {
  const {
    className,
    cnButton,
    size = 'm',
    scrollable,
    idViewerModel,
    hasCylindo,
    ...restProps
  } = props;
  const { analytics } = useDeps();
  const viewerModel = PageProductStore.useViewerModel(idViewerModel);

  const handleToggle360 = useCallback(
    (e: MouseEvent, val: boolean) => {
      PageProductStore.change360(idViewerModel, val);

      analytics.dispatchEvent('card.show360', {
        isShow360: val,
      });
    },
    [analytics, idViewerModel],
  );

  useEffect(() => {
    PageProductStore.change360(idViewerModel, true);
  }, [idViewerModel]);

  return (
    <Container
      {...restProps}
      scrollable={scrollable}
      className={cn(
        styles.wrapper,
        { [styles.sizeM]: size === 'm', [styles.sizeS]: size === 's' },
        className,
      )}
    >
      {hasCylindo && (
        <Button
          className={cn(
            styles.button,
            styles.switcher,
            { [styles.actived]: viewerModel?.isOpen360 },
            cnButton,
          )}
          view='rounded'
          theme='white'
          onClick={(e) => handleToggle360(e, !viewerModel?.isOpen360)}
        >
          <Switch className={styles.switch} name='view360' checked={viewerModel?.isOpen360} />
          <div className={styles.buttonText}>360°</div>
        </Button>
      )}

      {!hasCylindo && <div className={styles.placeholder} />}
    </Container>
  );
};

export default memo(Buttons);
